import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';
import { match } from 'ts-pattern';

import type { JobPost } from '~/api/jobPosts/types';

import { useApplyExternalJobPost } from '~/api/jobPosts/useApplyExternalJobPost';
import { useBookmarkJobPost } from '~/api/jobPosts/useBookmarkJobPost';
import { useJobPost } from '~/api/jobPosts/useJobPost';
import { useUser } from '~/api/user';
import {
  Alert,
  AlertDialog,
  BookmarkButton,
  Button,
  Card,
  Container,
  Dialog,
  Header,
  Menu,
  Redirect,
  RichTextRenderer,
  StatusTag,
  Tag,
} from '~/components';
import { SearchParamKeys } from '~/constants/url';
import { EnableCommunityProfileDialog } from '~/features/community';
import { useDialog } from '~/hooks/useDialog';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { qs } from '~/utils/searchParams';
import { toast } from '~/utils/toast';
import { getIllustrationByActivityId } from '~/utils/user';

import type { loader } from './JobPostDetail.loader';

import { type JobPostSpecKey, useJobPostSpecs } from '../JobPost.hooks';
import { JobPostApplyDialog } from '../JobPostApplyDialog/JobPostApplyDialog';
import { JobPostClientLogo } from '../JobPostClientLogo/JobPostClientLogo';
import { JobPostCancelledConfirmDialog } from '../JobPostDropdownMenu/JobPostCancelledConfirmDialog';
import { useOnCancelledJobPost, useOnFilledJobPost } from '../JobPostDropdownMenu/JobPostDropdownMenu.hooks';
import { JobPostFilledConfirmDialog } from '../JobPostDropdownMenu/JobPostFilledConfirmDialog';
import {
  activitiesStyle,
  activityTagStyle,
  alertStyle,
  cardStyle,
  clientNameStyle,
  clientSourceStyle,
  clientWrapperStyle,
  contentStyle,
  createdAtStyle,
  headerWrapperStyle,
  specificationsStyle,
  specificationStyle,
  specificationTextStyle,
  statusTagStyle,
  wrapperStyle,
} from './JobPostDetail.css';

export const JobPostDetailGuard = () => {
  const loaderData = useLoaderData<typeof loader>();
  const { data: jobPost } = useJobPost(loaderData.id);

  if (!jobPost) {
    return <Redirect to={routes.jobPosts} />;
  }

  return <JobPostDetail jobPost={jobPost} />;
};

type Props = {
  jobPost: JobPost;
};

const JobPostDetail = ({ jobPost }: Props) => {
  const { data: user } = useUser();
  const bookmarkMutation = useBookmarkJobPost(jobPost.id);
  const applyExternalJobPostMutation = useApplyExternalJobPost(jobPost.id);

  const internalApplyDialog = useDialog();
  const applySuccessHandle = AlertDialog.useHandle();
  const enableCommunityProfileHandle = Dialog.useHandle();

  const jobPostSpecs = useJobPostSpecs(jobPost);
  const { confirmFilled, filledHandle, isPending: isFilledPending, onFilled } = useOnFilledJobPost(jobPost);
  const { cancelledHandle, confirmCancelled, isPending: isCancelledPending, onCancelled } = useOnCancelledJobPost(jobPost);
  const selectedJobPostSpecs: JobPostSpecKey[] = ['price', 'experienceLevel', 'region', 'locationType', 'period', 'closesAt'];

  const { t } = useTranslation(['common', 'jobPosts']);

  return (
    <>
      <Header.Root>
        <Header.Content title={jobPost.title} />

        <Header.Actions>
          {jobPost.userHasCreated ? (
            jobPost.status === 'Open' && (
              <>
                <Header.Action icon="CheckProgress" loading={isFilledPending} onClick={onFilled}>
                  {t('jobPosts:contextMenu.actions.filled')}
                </Header.Action>

                <Menu.Root disabled={isFilledPending || isCancelledPending}>
                  <Menu.Trigger
                    render={
                      <Button aria-label={t('common:actions.openMenu')} icon="MoreVert" loading={isFilledPending || isCancelledPending} />
                    }
                  />

                  <Menu.List>
                    <Menu.LinkItem
                      icon="Edit"
                      to={{
                        pathname: r(routes.editJobPost, { jobPostId: jobPost.id }),
                        search: qs({ [SearchParamKeys.REDIRECT_PATH]: location.pathname + location.search }),
                      }}
                    >
                      {t('jobPosts:contextMenu.actions.edit')}
                    </Menu.LinkItem>

                    <Menu.Item icon="CloseSmall" onClick={onCancelled}>
                      {t('jobPosts:contextMenu.actions.cancelled')}
                    </Menu.Item>
                  </Menu.List>
                </Menu.Root>
              </>
            )
          ) : (
            <>
              <BookmarkButton
                disabled={bookmarkMutation.isPending}
                isBookmarked={bookmarkMutation.variables !== undefined ? bookmarkMutation.variables : jobPost.userHasBookmarked}
                onBookmark={(bookmarked) =>
                  bookmarkMutation.mutate(bookmarked, {
                    onError: () => {
                      bookmarkMutation.reset();
                      toast.danger({ description: t('common:error') });
                    },
                  })
                }
              />

              {jobPost.source === 'Internal' ? (
                <Header.Action
                  disabled={jobPost.userHasApplied}
                  icon="ChatBubble"
                  onClick={() => (user.communityProfile.enabled ? internalApplyDialog.open() : enableCommunityProfileHandle.open(null))}
                >
                  {jobPost.userHasApplied ? t(`jobPosts:details.actions.alreadyApplied`) : t(`jobPosts:details.actions.apply`)}
                </Header.Action>
              ) : (
                <Header.ExternalLinkAction
                  href={jobPost.applyMode === 'ExternalUrl' ? jobPost.externalLink : `mailto:${jobPost.externalClientEmail}`}
                  icon={jobPost.applyMode === 'ExternalUrl' ? 'Link' : 'Mail'}
                  onClick={(e) => {
                    if (!user.communityProfile.enabled) {
                      e.preventDefault();
                      enableCommunityProfileHandle.open(null);
                      return;
                    }

                    applyExternalJobPostMutation.mutate();
                  }}
                >
                  {t(`jobPosts:details.actions.apply`)}
                </Header.ExternalLinkAction>
              )}
            </>
          )}
        </Header.Actions>
      </Header.Root>

      <Container>
        {jobPost.userHasApplied && jobPost.source === 'Internal' && (
          <Alert announce className={alertStyle} icon="Check" status="success" title={t(`jobPosts:details.info.alreadyApplied.title`)}>
            {t(`jobPosts:details.info.alreadyApplied.description`)}
          </Alert>
        )}

        {jobPost.userHasCreated && jobPost.status === 'Open' && (
          <Alert announce className={alertStyle} icon="Info" status="info" title={t(`jobPosts:details.info.userHasCreated.title`)}>
            {t(`jobPosts:details.info.userHasCreated.description`)}
          </Alert>
        )}

        <div className={wrapperStyle}>
          <div className={contentStyle}>
            <div className={activitiesStyle}>
              {jobPost.activityIds.map((activityId) => (
                <Tag
                  className={activityTagStyle}
                  illustration={getIllustrationByActivityId(activityId)}
                  key={activityId}
                  text={t(`common:activities.${activityId}.label`)}
                />
              ))}
            </div>
            <RichTextRenderer content={jobPost.description} />
          </div>

          <Card.Root className={cardStyle} variant="primary-light">
            <div className={headerWrapperStyle}>
              <JobPostClientLogo jobPost={jobPost} />

              <div className={clientWrapperStyle}>
                <div className={clientNameStyle}>{jobPost.clientName}</div>

                <p className={clientSourceStyle}>
                  {jobPost.source === 'External' ? t('jobPosts:card.postedByCS') : t('jobPosts:card.postedByFreelancer')}
                </p>
              </div>
            </div>

            {jobPost.userHasCreated && (
              <StatusTag
                className={statusTagStyle}
                icon={match(jobPost.status)
                  .with('Open', () => 'HourglassTop' as const)
                  .with('Filled', () => 'Check' as const)
                  .with('Cancelled', () => 'CloseSmall' as const)
                  .exhaustive()}
                label={t(`jobPosts:fields.status.options.${jobPost.status}`)}
                status={match(jobPost.status)
                  .with('Open', () => 'info' as const)
                  .with('Filled', () => 'success' as const)
                  .with('Cancelled', () => 'danger' as const)
                  .exhaustive()}
              />
            )}

            <div className={specificationsStyle}>
              {selectedJobPostSpecs
                .filter((key) => jobPostSpecs[key].value)
                .map((key) => (
                  <div className={specificationStyle} key={key}>
                    <p>{jobPostSpecs[key].label}</p>

                    <p className={specificationTextStyle}>{jobPostSpecs[key].value}</p>
                  </div>
                ))}
            </div>

            <div className={createdAtStyle}>
              {t('jobPosts:fields.createdAt.label')} {dayjs(jobPost.createdAt).format('DD/MM/YYYY')}
            </div>
          </Card.Root>
        </div>
      </Container>

      {jobPost.applyMode === 'InternalEmail' && (
        <>
          <JobPostApplyDialog
            isOpen={internalApplyDialog.isOpen}
            jobPost={jobPost}
            onClose={(success) => {
              internalApplyDialog.close();
              if (success) applySuccessHandle.open(null);
            }}
          />
          <AlertDialog.Root handle={applySuccessHandle}>
            <AlertDialog.Body icon="CheckProgress" status="success">
              <AlertDialog.Title>{t('jobPosts:apply.successDialog.title')}</AlertDialog.Title>

              <AlertDialog.Description>{t('jobPosts:apply.successDialog.description')}</AlertDialog.Description>

              <AlertDialog.Actions>
                <AlertDialog.Close render={<Button>{t('jobPosts:apply.successDialog.buttonText')}</Button>} />
              </AlertDialog.Actions>
            </AlertDialog.Body>
          </AlertDialog.Root>
        </>
      )}

      <AlertDialog.Root handle={filledHandle}>
        <JobPostFilledConfirmDialog loading={isFilledPending} onConfirm={confirmFilled} />
      </AlertDialog.Root>

      <AlertDialog.Root handle={cancelledHandle}>
        <JobPostCancelledConfirmDialog loading={isCancelledPending} onConfirm={confirmCancelled} />
      </AlertDialog.Root>

      <Dialog.Root handle={enableCommunityProfileHandle}>
        <EnableCommunityProfileDialog />
      </Dialog.Root>
    </>
  );
};
